import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Pencil, Trash2, Plus, LogOut, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { formatPKR } from "../lib/format";

const emptyForm = {
  name: "",
  brand: "",
  description: "",
  price: 0,
  gender: "unisex",
  category: "Eau de Parfum",
  volume_ml: 100,
  notes_top: "",
  notes_middle: "",
  notes_base: "",
  images: "",
  featured: false,
  stock: 10,
};

const csvToArr = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
const arrToCsv = (a) => (Array.isArray(a) ? a.join(", ") : "");

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/products");
    setProducts(data);
    setLoading(false);
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user]);

  if (user === null) {
    return <div className="py-32 text-center text-ash">Checking session…</div>;
  }
  if (!user || user.role !== "admin") {
    return <Navigate to="/admin/login" replace />;
  }

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      ...p,
      notes_top: arrToCsv(p.notes_top),
      notes_middle: arrToCsv(p.notes_middle),
      notes_base: arrToCsv(p.notes_base),
      images: arrToCsv(p.images),
    });
    setOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        brand: form.brand,
        description: form.description,
        price: Number(form.price),
        gender: form.gender,
        category: form.category,
        volume_ml: Number(form.volume_ml),
        notes_top: csvToArr(form.notes_top),
        notes_middle: csvToArr(form.notes_middle),
        notes_base: csvToArr(form.notes_base),
        images: csvToArr(form.images),
        featured: !!form.featured,
        stock: Number(form.stock),
      };
      if (editingId) {
        await api.put(`/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setOpen(false);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    const uploadToast = toast.loading("Uploading images...");
    try {
      const urls = [];
      for (const file of files) {
        if (!["image/jpeg", "image/png"].includes(file.type)) {
          toast.error(`Skipping ${file.name}: only JPEG/PNG allowed.`);
          continue;
        }
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await api.post("/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        // Prepend the backend base URL to the image path if needed (e.g. /api/images/...)
        urls.push(api.defaults.baseURL ? `${api.defaults.baseURL.replace('/api', '')}${data.url}` : data.url);
      }
      
      if (urls.length > 0) {
        setForm((prev) => {
          const existing = prev.images ? prev.images.split(",").map(s => s.trim()).filter(Boolean) : [];
          return { ...prev, images: [...existing, ...urls].join(", ") };
        });
        toast.success("Images uploaded", { id: uploadToast });
      } else {
        toast.dismiss(uploadToast);
      }
    } catch (err) {
      toast.error("Upload failed", { id: uploadToast });
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-10 md:py-14">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-gold-soft pb-6 mb-8">
        <div>
          <div className="eyebrow mb-2">Admin</div>
          <h1 className="font-serif text-4xl md:text-5xl text-ink">Product Library</h1>
          <p className="text-sm text-ash mt-2">Signed in as {user.email}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-add-product-btn"
            onClick={openCreate}
            className="inline-flex items-center gap-2 bg-ink text-cream h-11 px-5 text-xs uppercase tracking-[0.22em] font-medium hover:bg-gold transition-colors"
          >
            <Plus size={14} />
            Add product
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="inline-flex items-center gap-2 border border-gold-soft text-ink h-11 px-5 text-xs uppercase tracking-[0.22em] font-medium hover:border-ink transition-colors"
          >
            <LogOut size={14} />
            Logout
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-16 bg-sand animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="border border-gold-soft bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-products-table">
            <thead className="bg-sand">
              <tr className="text-left">
                <th className="px-4 py-3 eyebrow text-ash w-16">Image</th>
                <th className="px-4 py-3 eyebrow text-ash">Name</th>
                <th className="px-4 py-3 eyebrow text-ash">Brand</th>
                <th className="px-4 py-3 eyebrow text-ash">Gender</th>
                <th className="px-4 py-3 eyebrow text-ash">Price</th>
                <th className="px-4 py-3 eyebrow text-ash">Stock</th>
                <th className="px-4 py-3 eyebrow text-ash">Featured</th>
                <th className="px-4 py-3 eyebrow text-ash w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-gold-soft hover:bg-cream/50">
                  <td className="px-4 py-3">
                    <div className="w-12 h-14 bg-sand overflow-hidden">
                      {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-serif text-lg text-ink">{p.name}</div>
                    <div className="text-xs text-ash">{p.volume_ml}ml · {p.category}</div>
                  </td>
                  <td className="px-4 py-3 text-graphite">{p.brand}</td>
                  <td className="px-4 py-3 capitalize text-graphite">{p.gender}</td>
                  <td className="px-4 py-3 tabular-nums">{formatPKR(p.price)}</td>
                  <td className="px-4 py-3 tabular-nums">{p.stock}</td>
                  <td className="px-4 py-3">
                    {p.featured ? <span className="text-gold text-xs uppercase tracking-widest">Yes</span> : <span className="text-ash text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`admin-edit-${p.id}`}
                        onClick={() => openEdit(p)}
                        className="p-2 border border-gold-soft hover:border-ink transition"
                        aria-label="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        data-testid={`admin-delete-${p.id}`}
                        onClick={() => handleDelete(p)}
                        className="p-2 border border-gold-soft hover:border-red-500 hover:text-red-600 transition"
                        aria-label="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-cream border-gold-soft max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl text-ink">
              {editingId ? "Edit Product" : "New Product"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" required>
                <input
                  data-testid="form-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input"
                />
              </Field>
              <Field label="Brand" required>
                <input
                  data-testid="form-brand"
                  required
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                  className="input"
                />
              </Field>
            </div>

            <Field label="Description">
              <textarea
                data-testid="form-description"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="input"
              />
            </Field>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Price (₨)">
                <input
                  data-testid="form-price"
                  type="number"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="input"
                />
              </Field>
              <Field label="Volume (ml)">
                <input
                  data-testid="form-volume"
                  type="number"
                  min="1"
                  value={form.volume_ml}
                  onChange={(e) => setForm({ ...form, volume_ml: e.target.value })}
                  className="input"
                />
              </Field>
              <Field label="Stock">
                <input
                  data-testid="form-stock"
                  type="number"
                  min="0"
                  value={form.stock}
                  onChange={(e) => setForm({ ...form, stock: e.target.value })}
                  className="input"
                />
              </Field>
              <Field label="Gender">
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="form-gender" className="input h-10 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="men">Men</SelectItem>
                    <SelectItem value="women">Women</SelectItem>
                    <SelectItem value="unisex">Unisex</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Category">
              <input
                data-testid="form-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="input"
                placeholder="Eau de Parfum"
              />
            </Field>

            <Field label="Top notes (comma separated)">
              <input value={form.notes_top} onChange={(e) => setForm({ ...form, notes_top: e.target.value })} className="input" />
            </Field>
            <Field label="Heart notes">
              <input value={form.notes_middle} onChange={(e) => setForm({ ...form, notes_middle: e.target.value })} className="input" />
            </Field>
            <Field label="Base notes">
              <input value={form.notes_base} onChange={(e) => setForm({ ...form, notes_base: e.target.value })} className="input" />
            </Field>
            <Field label="Image URLs (comma separated)">
              <textarea
                data-testid="form-images"
                rows={2}
                value={form.images}
                onChange={(e) => setForm({ ...form, images: e.target.value })}
                className="input mb-3"
                placeholder="https://... , https://..."
              />
              <div className="flex flex-col gap-1.5 border-t border-gold-soft pt-3">
                <label className="text-[11px] uppercase tracking-wider text-ink font-medium">Or upload directly (JPEG/PNG)</label>
                <input 
                  type="file" 
                  accept="image/jpeg, image/png" 
                  multiple 
                  onChange={handleFileUpload} 
                  className="text-sm text-ash file:mr-4 file:py-2 file:px-4 file:rounded-none file:border file:border-ink file:text-xs file:uppercase file:tracking-widest file:font-medium file:bg-transparent file:text-ink hover:file:bg-ink hover:file:text-white transition cursor-pointer"
                />
              </div>
            </Field>

            <label className="inline-flex items-center gap-2 text-sm text-ink cursor-pointer">
              <input
                data-testid="form-featured"
                type="checkbox"
                checked={!!form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                className="w-4 h-4 accent-gold"
              />
              Featured on homepage
            </label>

            <DialogFooter className="pt-4 gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="border border-gold-soft px-5 h-11 text-xs uppercase tracking-[0.22em] hover:border-ink transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="form-submit"
                disabled={saving}
                className="bg-ink text-cream px-6 h-11 text-xs uppercase tracking-[0.22em] hover:bg-gold transition disabled:opacity-50"
              >
                {saving ? "Saving…" : editingId ? "Update" : "Create"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <style>{`
        .input { width: 100%; padding: 0.6rem 0.85rem; background: #FFFFFF; border: 1px solid #E5DFD3; color: #1A1A1A; outline: none; transition: border-color 200ms; font-size: 0.875rem; }
        .input:focus { border-color: #C19A5B; }
      `}</style>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="eyebrow text-ash text-[10px]">
      {label}{required && <span className="text-gold ml-1">*</span>}
    </label>
    {children}
  </div>
);

export default AdminDashboard;
